/**
 * CamereList Component - Hedita Design System
 * Feature: Strutture - Lista camere
 */

import { useState } from "react";
import { Plus, Search, Bed, Building2, Filter, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useCamereByStruttura, useDeleteCamera } from "../hooks";
import type { Camera } from "@/types";

const STATO_CONFIG: Record<string, { bg: string; color: string; border: string; label: string }> = {
  "Disponibile": { bg: "bg-emerald-500/10", color: "text-emerald-400", border: "border-emerald-500/20", label: "Disponibile" },
  "Occupata": { bg: "bg-rose-500/10", color: "text-rose-400", border: "border-rose-500/20", label: "Occupata" },
  "In pulizia": { bg: "bg-amber-500/10", color: "text-amber-400", border: "border-amber-500/20", label: "In pulizia" },
  "Manutenzione": { bg: "bg-slate-500/10", color: "text-slate-400", border: "border-slate-500/20", label: "Manutenzione" },
};

interface CamereListProps {
  strutturaId?: string;
}

export function CamereList({ strutturaId }: CamereListProps) {
  const [search, setSearch] = useState("");
  const [statoFilter, setStatoFilter] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  
  const { data: camere = [], isLoading } = useCamereByStruttura(strutturaId);
  const deleteCamera = useDeleteCamera();

  const filtered = camere.filter(c => {
    const matchSearch = !search || c.nome?.toLowerCase().includes(search.toLowerCase());
    const matchStato = !statoFilter || c.stato === statoFilter;
    return matchSearch && matchStato;
  });

  const stati = [...new Set(camere.map(c => c.stato).filter(Boolean))];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Bed className="h-5 w-5 text-primary" />
            Camere
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gestione camere e unità</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2.5 rounded-xl border transition-all ${
              showFilters ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground"
            }`}
          >
            <Filter className="h-4 w-4" />
          </button>
          <button 
            onClick={() => window.location.href = strutturaId ? `/strutture/camere/nuovo?struttura=${strutturaId}` : "/strutture/camere/nuovo"}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-primary/90 transition-all shadow-sm"
          >
            <Plus className="h-4 w-4" /> Nuova camera
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && stati.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-secondary/30 rounded-xl border border-border/50">
          <button 
            onClick={() => setStatoFilter(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              !statoFilter ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"
            }`}
          >
            Tutte
          </button>
          {stati.map(s => {
            const cfg = STATO_CONFIG[s] || { bg: "bg-muted/30", color: "text-muted-foreground", border: "border-border", label: s };
            const active = statoFilter === s;
            return (
              <button 
                key={s} 
                onClick={() => setStatoFilter(active ? null : s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all flex items-center gap-1.5 ${
                  active ? `${cfg.bg} ${cfg.color} ${cfg.border}` : "border-border text-muted-foreground"
                }`}
              >
                {cfg.label}
              </button>
            );
          })}
          {statoFilter && (
            <button onClick={() => setStatoFilter(null)} className="p-1.5 rounded-lg text-muted-foreground">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Cerca camera..."
          className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:border-primary/50 transition-all"
        />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-secondary/50 border border-border/50 flex items-center justify-center mx-auto mb-4">
            <Bed className="h-8 w-8 text-muted-foreground/40" />
          </div>
          <p className="font-medium text-foreground mb-1">Nessuna camera</p>
          <p className="text-sm text-muted-foreground mb-4">Aggiungi la prima camera</p>
          <button 
            onClick={() => window.location.href = "/strutture/camere/nuovo"}
            className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-all"
          >
            Nuova camera
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((c: Camera) => {
            const cfg = STATO_CONFIG[c.stato || "Disponibile"] || STATO_CONFIG["Disponibile"];
            
            return (
              <div 
                key={c.id} 
                onClick={() => window.location.href = `/strutture/camere/${c.id}`}
                className="bg-card border border-border rounded-2xl p-4 hover:border-primary/30 transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-secondary/50 border border-border/50 flex items-center justify-center flex-shrink-0">
                    <Bed className="h-5 w-5 text-muted-foreground/60" />
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-lg font-medium border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                    {cfg.label}
                  </span>
                </div>
                
                <h3 className="font-semibold text-foreground text-sm mb-1 truncate">{c.nome}</h3>
                {c.struttura_nome && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {c.struttura_nome}
                  </p>
                )}
                
                {(c.posti_letto || c.metri_quadri) && (
                  <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                    {c.posti_letto && <span>{c.posti_letto} posti</span>}
                    {c.metri_quadri && <span>{c.metri_quadri} m²</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
